<script>
  import { page, router, useForm } from '@inertiajs/svelte'
  import dayjs from 'dayjs'
  import Layout from '../../layouts/layout.svelte'
  import PageHeader from '../../components/page_header.svelte'
  import FormLayout from '../../components/form_layout.svelte'
  import FormSection from '../../components/form_section.svelte'
  import ScheduleEditor from '../../components/schedule_editor.svelte'
  import TargetingEditor from '../../components/targeting_editor.svelte'
  import ImageSelector from '../../components/image_selector.svelte'
  import Modal from '../../components/modal.svelte'
  import TextField from '../../components/form/text_field.svelte'
  import TextareaField from '../../components/form/textarea_field.svelte'
  import SelectField from '../../components/form/select_field.svelte'
  import CheckboxField from '../../components/form/checkbox_field.svelte'
  import ToggleField from '../../components/form/toggle_field.svelte'
  import NumberField from '../../components/form/number_field.svelte'
  import EmojiTextField from '../../components/form/emoji_text_field.svelte'
  import SubmitButton from '../../components/form/submit_button.svelte'
  import ClickableImage from '../../components/lightbox/clickable_image.svelte'
  import ClickableVideo from '../../components/lightbox/clickable_video.svelte'
  import CreateExampleModal from '../../components/quests/create_example_modal.svelte'
  import RatedPostsModal from '../../components/quests/rated_posts_modal.svelte'
  import GeneratedQuests from '../../components/quests/generated_quests.svelte'
  import ChangeVideoThumbnailModal from '../../components/quests/change_video_thumbnail_modal.svelte'
  import ActionToast from '../../components/quest_submissions/action_toast.svelte'
  import { copyShareLink } from '../../lib/clipboard.js'
  import { MAX_VIDEO_DURATION_LABEL_SECONDS } from '../../lib/video_file.js'
  import { liveValidate } from '../../lib/live_validation.svelte.js'
  import { Button } from '../../components/ui'
  import { confirm } from '../../components/confirm.js'

  // The form edits the template in the shape the server hands over and submits it back as
  // typed; AdminSite::QuestTemplateFormParams nulls the unchosen award, defaults radii and
  // builds the `_replace` rows. Validation is the server's: each change is checked live by a
  // Precognition request to the real create/update action, errors keyed `quest_template.<field>`.
  let {
    quest_template,
    options = {},
    quests = null,
    can_edit_thumbnails = false,
    approved_submissions = null,
    rated_submissions = null,
    curation_levels = [],
    // The list URL (filters included) the form was opened from.
    back_url = '/quests',
    // The template a clone was made from; a failed save returns to the clone, not a blank form.
    parent_id = null
  } = $props()

  const isEdit = !!quest_template.id
  const url = isEdit ? `/quests/${quest_template.id}` : '/quests'
  const PROMPT_SOFT_LIMIT = 250
  const DEFAULT_RADIUS_M = options.default_location_radius_m

  const form = useForm({
    quest_template: {
      name: quest_template.name,
      display_name: quest_template.display_name,
      area_id: quest_template.area_id ?? '',
      base_award_type: quest_template.base_award_type,
      point_source_id: quest_template.point_source_id ?? '',
      reward_adventure_id: quest_template.reward_adventure_id ?? '',
      prompt: quest_template.prompt,
      prompt_description: quest_template.prompt_description,
      emoji: quest_template.emoji,
      capture_mode: quest_template.capture_mode,
      auto_validate: quest_template.auto_validate,
      requires_location: quest_template.requires_location,
      location_radius_m: quest_template.location_radius_m,
      active: quest_template.active,
      banner_image_id: quest_template.banner_image?.id ?? null,
      public_bonus_point_source_id: quest_template.public_bonus_point_source_id ?? '',
      early_bonus_point_source_id: quest_template.early_bonus_point_source_id ?? '',
      early_bonus_hours: quest_template.early_bonus_hours,
      visible_schedule_attributes: quest_template.visible_schedule_attributes,
      active_schedule_attributes: quest_template.active_schedule_attributes,
      quest_rules: quest_template.quest_rules,
      category_ids: quest_template.category_ids,
      sub_category_ids: quest_template.sub_category_ids,
      places: quest_template.places,
      excluded_sub_category_ids: quest_template.excluded_sub_category_ids,
      excluded_place_ids: quest_template.excluded_place_ids
    },
    return_to: back_url,
    parent_id
  }).withPrecognition(isEdit ? 'patch' : 'post', url)
  liveValidate(form, 'quest_template')

  // The banner picker works in image objects; the form sends the picked id.
  let bannerImages = $state(quest_template.banner_image ? [quest_template.banner_image] : [])
  $effect(() => {
    $form.quest_template.banner_image_id = bannerImages[0]?.id ?? null
  })

  // Which award picker the chosen base award shows, as the server defines it.
  const awardFields = $derived(options.base_awards.find((award) => award.value === $form.quest_template.base_award_type)?.fields ?? [])

  // Mirrors the Visible recurrence onto Active so the two editors agree on screen (the
  // server refuses a template whose schedules differ).
  $effect(() => {
    const template = $form.quest_template
    if (template.active_schedule_attributes.recur_on !== template.visible_schedule_attributes.recur_on) {
      $form.quest_template.active_schedule_attributes.recur_on = template.visible_schedule_attributes.recur_on
    }
  })

  const errorFor = (key) => $form.errors[`quest_template.${key}`]

  const submit = () => {
    if (isEdit) $form.patch(url, { preserveScroll: 'errors' })
    else $form.post(url, { preserveScroll: 'errors' })
  }

  // -- Rules editor -----------------------------------------------------------
  const addRule = () => ($form.quest_template.quest_rules = [...$form.quest_template.quest_rules, { emoji: '', text: '' }])
  const removeRule = (index) => ($form.quest_template.quest_rules = $form.quest_template.quest_rules.filter((_, i) => i !== index))
  const moveRule = (index, delta) => {
    const rules = [...$form.quest_template.quest_rules]
    const target = index + delta
    if (target < 0 || target >= rules.length) return
    const [rule] = rules.splice(index, 1)
    rules.splice(target, 0, rule)
    $form.quest_template.quest_rules = rules
  }

  // -- Preview tab ------------------------------------------------------------
  let tab = $state('form')
  let markTarget = $state(null)
  let loadingSubmissions = $state(false)

  const openMarkModal = (quest) => {
    markTarget = quest
    loadingSubmissions = true
    router.reload({
      only: ['approved_submissions'],
      data: { modal_quest_id: quest.id },
      onFinish: () => (loadingSubmissions = false)
    })
  }

  const markExample = (submissionId, example) => {
    router.patch(
      `/quest-submissions/${submissionId}/mark_example`,
      { example },
      {
        onSuccess: () => {
          markTarget = null
          router.reload({ only: ['quests'] })
        }
      }
    )
  }

  const unmarkExample = async (submissionId) => {
    const confirmed = await confirm({
      title: 'Remove this Example?',
      variant: 'error',
      body: "The seeded submission stays but is no longer pinned as the quest's Example."
    })
    if (!confirmed) return
    markExample(submissionId, false)
  }

  // -- Create Example / curator posts / rated posts -------------------------------
  let createOpen = $state(false)
  let createQuestId = $state(null)
  let createMode = $state('example')
  let ratedTarget = $state(null)
  let ratedLoadedFor = $state(null)
  let rateBusyId = $state(null)
  let ratedError = $state(null)

  // Re-picking a poster frame is a Moment#manage? action (QuestsController#edit).
  const canEditThumbnails = $derived(!!can_edit_thumbnails)

  const openCreate = (quest, mode) => {
    createQuestId = quest.id
    createMode = mode
    createOpen = true
  }

  const reloadRated = () => {
    if (!ratedTarget) return
    const questId = ratedTarget.id
    router.reload({
      only: ['rated_submissions', 'quests', 'flash'],
      data: { rated_quest_id: questId },
      onSuccess: () => (ratedLoadedFor = questId),
      // Leaving it unloaded keeps the previous quest's rows (and their rating
      // buttons) from showing under this quest's heading.
      onError: () => (ratedError = 'Could not load rated posts. Close and try again.')
    })
  }

  const openRatedPosts = (quest) => {
    ratedTarget = quest
    ratedLoadedFor = null
    ratedError = null
    reloadRated()
  }

  // -- Video thumbnails / share links -----------------------------------------
  let thumbnailTarget = $state(null)
  let toast = $state(null)

  const copyLink = async (url) => (toast = await copyShareLink(url))

  // The props a write from the Generated Quests tab refreshes on its redirect back (this
  // URL carries rated_quest_id while the rated posts modal is open).
  const tabProps = () => (ratedTarget ? ['quests', 'rated_submissions'] : ['quests'])

  // The redirect back refreshes this quest's rated posts under `rated_quest_id`;
  // the flash carries "Rated X!" / "Rating cleared.".
  const rate = (submission, level) => {
    ratedError = null
    router.patch(
      submission.rate_url,
      { level },
      {
        only: [...tabProps(), 'flash'],
        preserveState: true,
        preserveScroll: true,
        onStart: () => (rateBusyId = submission.id),
        onError: (errors) => (ratedError = ['Rating failed', errors.review || errors.base].filter(Boolean).join(' — ')),
        onNetworkError: () => (ratedError = 'Rating failed'),
        onFinish: () => (rateBusyId = null)
      }
    )
  }

  const clearRating = async (submission) => {
    const confirmed = await confirm({
      title: "Clear this post's rating?",
      confirmLabel: 'Clear',
      variant: 'error',
      body: "It drops out of the rated posts at the front of Quest Detail and out of the community feed's curated lane."
    })
    if (confirmed) rate(submission, 'none')
  }
</script>

<Layout>
  <PageHeader
    title={isEdit ? 'Edit Quest' : 'New Quest'}
    subtitle="Weekly capture challenge — setup, rules, schedules, eligible places, and go-live."
  />

  {#if isEdit}
    <div role="tablist" class="tabs tabs-lift mb-6">
      <button type="button" role="tab" class="tab" class:tab-active={tab === 'form'} onclick={() => (tab = 'form')}>Form</button>
      <button type="button" role="tab" class="tab" class:tab-active={tab === 'preview'} onclick={() => (tab = 'preview')}>Generated Quests</button>
    </div>
  {/if}

  {#if tab === 'form'}
    <FormLayout
      onsubmit={(event) => {
        event.preventDefault()
        submit()
      }}
    >
      <FormSection title="Quest Setup">
        <TextField bind:value={$form.quest_template.name} label="Name (Internal)" name="name" error={errorFor('name')} required />
        <TextField bind:value={$form.quest_template.display_name} label="Display Name" name="display_name" error={errorFor('display_name')} required />
        <SelectField bind:value={$form.quest_template.area_id} label="College Town" name="area_id" options={options.areas} error={errorFor('area_id')} required />
        <div>
          <TextareaField bind:value={$form.quest_template.prompt} label="Short Description" name="prompt" placeholder="Take a photo of…" error={errorFor('prompt')} required />
          <p class="text-xs mt-1 {$form.quest_template.prompt.length > PROMPT_SOFT_LIMIT ? 'text-error' : 'text-base-content/60'}">
            {$form.quest_template.prompt.length} characters
          </p>
        </div>
        <TextareaField
          bind:value={$form.quest_template.prompt_description}
          label="Tips (Optional)"
          name="prompt_description"
          placeholder="Shown under the Tips heading on the quest screen…"
          error={errorFor('prompt_description')}
        />
        <TextField
          bind:value={$form.quest_template.emoji}
          label="Emoji (Optional)"
          name="emoji"
          help={`Shown next to the quest title in the app. Up to ${options.emoji_max_length} characters.`}
          error={errorFor('emoji')}
        />
        <div>
          <ImageSelector bind:value={bannerImages} label="Banner Image (Optional)" multiple={false} />
          {#if errorFor('banner_image_id')}
            <p class="text-error text-sm mt-1">{errorFor('banner_image_id')}</p>
          {:else}
            <p class="label whitespace-normal">Shown as the quest's banner in the app. Selecting a new image replaces the current one.</p>
          {/if}
        </div>
        <SelectField
          bind:value={$form.quest_template.capture_mode}
          label="Capture Mode"
          name="capture_mode"
          options={options.capture_modes ?? []}
          help={`What the user must capture to complete the quest. Video Examples uploaded here can run up to ${MAX_VIDEO_DURATION_LABEL_SECONDS} seconds.`}
          error={errorFor('capture_mode')}
          required
        />
        <SelectField
          bind:value={$form.quest_template.base_award_type}
          label="Base award"
          name="base_award_type"
          options={options.base_awards}
          help="What completing the quest earns: points, or a reward-store item. The bonuses below are points either way."
          required
        />
        {#if awardFields.includes('reward_adventure_id')}
          <SelectField
            bind:value={$form.quest_template.reward_adventure_id}
            label="Store item reward"
            name="reward_adventure_id"
            source="quest_rewards"
            sourceParams={{ area_id: $form.quest_template.area_id }}
            selected={quest_template.reward_adventure}
            placeholder={$form.quest_template.area_id ? 'Search store items…' : 'Select a college town first'}
            disabled={!$form.quest_template.area_id}
            help="Dropped into the user's My Rewards when the submission is approved, ready to redeem for 30 days, as if bought with points. Must be a store item in this college town; gift-card items aren't offered. Changing it here changes what every live quest from this template grants."
            error={errorFor('reward_adventure_id')}
            required
          />
        {:else}
          <SelectField
            bind:value={$form.quest_template.point_source_id}
            label="Point Source"
            name="point_source_id"
            options={options.point_sources}
            help="Points are awarded once when the user completes the quest."
            error={errorFor('point_source_id')}
            required
          />
        {/if}
        <SelectField
          bind:value={$form.quest_template.public_bonus_point_source_id}
          label="Public Bonus Point Source (Optional)"
          name="public_bonus_point_source_id"
          options={options.point_sources}
          placeholder="None"
          help="Extra points awarded at approval when the submission's Moment is public. Revoked if the user hides the Moment within 24 hours."
          error={errorFor('public_bonus_point_source_id')}
        />
        <SelectField
          bind:value={$form.quest_template.early_bonus_point_source_id}
          label="Early Bonus Point Source (Optional)"
          name="early_bonus_point_source_id"
          options={options.point_sources}
          placeholder="None"
          help="Extra points awarded at approval when the submission was made within the early bonus window after the quest became active."
          error={errorFor('early_bonus_point_source_id')}
        />
        {#if $form.quest_template.early_bonus_point_source_id}
          <NumberField
            bind:value={$form.quest_template.early_bonus_hours}
            label="Early Bonus Window (hours)"
            name="early_bonus_hours"
            help="How many hours after the quest becomes active the early bonus lasts."
            error={errorFor('early_bonus_hours')}
            required
          />
        {/if}
        <CheckboxField
          bind:checked={$form.quest_template.auto_validate}
          label="Auto-validate"
          name="auto_validate"
          help="Submissions skip admin review and are approved instantly."
        />
        <CheckboxField
          bind:checked={$form.quest_template.requires_location}
          label="Requires location"
          name="requires_location"
          help="When on, the iOS app enforces that the user is at the place. (Server does not validate.)"
        />
        {#if $form.quest_template.requires_location}
          <NumberField
            bind:value={$form.quest_template.location_radius_m}
            label="Location radius (meters)"
            name="location_radius_m"
            help={`Minimum geofence radius. A place whose own Checkin Radius is larger keeps the larger value unless you turn on Override for it under Eligible Places. Leave blank to use the ${DEFAULT_RADIUS_M}m server default.`}
            error={errorFor('location_radius_m')}
          />
        {/if}
      </FormSection>

      <FormSection title="Rules">
        <div class="space-y-3">
          {#each $form.quest_template.quest_rules as rule, ruleIndex (ruleIndex)}
            <div class="flex items-end gap-2">
              <div class="flex flex-col">
                <Button variant="ghost" size="xs" square icon="chevron_up" disabled={ruleIndex === 0} onclick={() => moveRule(ruleIndex, -1)} aria-label="Move rule up" />
                <Button variant="ghost" size="xs" square icon="chevron_down" disabled={ruleIndex === $form.quest_template.quest_rules.length - 1} onclick={() => moveRule(ruleIndex, 1)} aria-label="Move rule down" />
              </div>
              <div class="grow">
                <EmojiTextField
                  bind:emoji={rule.emoji}
                  bind:text={rule.text}
                  name={`rule_${ruleIndex}`}
                  emojiLabel="Emoji"
                  textLabel="Rule"
                />
              </div>
              <Button variant="ghost" size="sm" class="text-error" onclick={() => removeRule(ruleIndex)} aria-label="Remove rule">Remove</Button>
            </div>
          {/each}
          {#if errorFor('quest_rules')}
            <p class="text-error text-sm">{errorFor('quest_rules')}</p>
          {/if}
          <Button variant="default" outline size="sm" icon="add" onclick={addRule}>Add rule</Button>
        </div>
      </FormSection>

      <FormSection title="Schedules">
        {#if isEdit}
          <p class="text-sm text-warning">
            Schedule edits move the quests already generated from this template — including moving a date, which
            carries the occurrence (and any submissions) to the new date. An occurrence removed outright with
            submissions stays live and is listed when you save; brand-new occurrences appear a few seconds after
            saving.
          </p>
        {/if}
        <p class="text-sm text-base-content/60">
          The <strong>Visible</strong> schedule is when the quest appears in the app (upcoming + active windows).
          The <strong>Active</strong> schedule is when users can submit. Both schedules must share the same
          recurrence type — changing the Visible recurrence updates Active to match.
        </p>
        <div class="grid gap-6 xl:grid-cols-2">
          <div class="min-w-0">
            <h3 class="font-bold mb-2">Visible Schedule</h3>
            <ScheduleEditor bind:value={$form.quest_template.visible_schedule_attributes} timeZones={options.time_zones ?? []} error={errorFor('visible_schedule_attributes')} />
          </div>
          <div class="min-w-0">
            <h3 class="font-bold mb-2">Active Schedule</h3>
            <ScheduleEditor bind:value={$form.quest_template.active_schedule_attributes} timeZones={options.time_zones ?? []} error={errorFor('active_schedule_attributes')} />
          </div>
        </div>
      </FormSection>

      <FormSection title="Eligible Places">
        <TargetingEditor
          bind:value={$form.quest_template}
          categories={options.categories}
          subCategories={options.sub_categories}
          placeSourceParams={{ area_id: $form.quest_template.area_id, checkin_enabled: true }}
          allowPlaceRadius={$form.quest_template.requires_location}
          defaultRadiusM={$form.quest_template.location_radius_m || DEFAULT_RADIUS_M}
          error={errorFor('excluded_place_ids')}
        />
        {#if isEdit && (quest_template.eligible_places ?? []).length > 0}
          <div>
            <h3 class="font-bold mb-2">Currently eligible places</h3>
            <div class="flex flex-wrap gap-1">
              {#each quest_template.eligible_places as place (place.id)}
                <span class="badge badge-ghost">{place.display_name || place.name}</span>
              {/each}
            </div>
          </div>
        {/if}
      </FormSection>

      <FormSection title={isEdit ? 'Save Your Changes' : 'Set Live'}>
        <ToggleField bind:checked={$form.quest_template.active} label="Active" name="active" error={errorFor('active')} />
        <p class="text-sm text-base-content/60">
          {isEdit
            ? 'This quest template will be updated with the information above.'
            : 'This quest template will be set live.'}
        </p>
        {#if errorFor('base')}<p class="text-sm text-error">{errorFor('base')}</p>{/if}
      </FormSection>

      {#snippet footer()}
        <Button href={back_url} variant="ghost" size="sm">Cancel</Button>
        <SubmitButton processing={$form.processing} label={isEdit ? 'Save Changes' : 'Set Live'} full={false} />
      {/snippet}
    </FormLayout>
  {:else}
    <GeneratedQuests
      {quests}
      onratedposts={openRatedPosts}
      onpickexample={openMarkModal}
      oncreateexample={(quest) => openCreate(quest, 'example')}
      onunmark={unmarkExample}
      onthumbnail={canEditThumbnails ? (target) => (thumbnailTarget = target) : null}
      oncopylink={copyLink}
    />
  {/if}

  <Modal open={!!markTarget} title="Mark an approved submission as Example" onclose={() => (markTarget = null)}>
    {#if markTarget}
      <p class="text-sm text-base-content/60 mb-2">
        Promote a real, already-approved submission to be the quest's Example.
      </p>
      <p class="text-sm mb-4">
        <span class="block">Quest: {markTarget.cycle_identifier}</span>
        {#if markTarget.prompt}<span class="block">{markTarget.prompt}</span>{/if}
      </p>

      {#if loadingSubmissions}
        <p class="text-base-content/60 py-3">Loading approved submissions…</p>
      {:else if (approved_submissions ?? []).length === 0}
        <p class="text-base-content/60 py-3">No approved submissions for this quest yet.</p>
      {:else}
        <div class="divide-y divide-base-200">
          {#each approved_submissions as submission (submission.id)}
            <div class="flex items-center gap-3 py-3">
              {#if submission.moment?.media_type === 'video' && submission.moment?.video_url}
                <ClickableVideo url={submission.moment.video_url} thumbnailUrl={submission.moment.thumbnail_url} thumbClass="w-20 h-20 object-contain bg-base-200 rounded-box" />
              {:else if submission.moment?.thumbnail_url}
                <ClickableImage url={submission.moment.thumbnail_url} title="Submission" thumbClass="w-20 h-20 object-contain bg-base-200 rounded-box" />
              {/if}
              <div class="grow text-sm">
                <div>{submission.user?.name}</div>
                <div class="text-xs text-base-content/50">{dayjs(submission.created_at).format('MMM D, YYYY h:mma')}</div>
              </div>
              <Button variant="primary" size="sm" onclick={() => markExample(submission.id, true)}>Mark</Button>
            </div>
          {/each}
        </div>
      {/if}
    {/if}
  </Modal>

  <RatedPostsModal
    open={!!ratedTarget && !createOpen && !thumbnailTarget}
    quest={ratedTarget}
    submissions={rated_submissions ?? []}
    curationLevels={curation_levels}
    loading={ratedLoadedFor !== ratedTarget?.id}
    busyId={rateBusyId}
    onrate={rate}
    onclear={clearRating}
    onaddpost={(quest) => openCreate(quest, 'curator_post')}
    onthumbnail={canEditThumbnails ? (target) => (thumbnailTarget = target) : null}
    onclose={() => (ratedTarget = null)}
  />
  {#if ratedError && ratedTarget}
    <div class="toast toast-top toast-center z-50">
      <div class="alert alert-error" role="alert">{ratedError}</div>
    </div>
  {/if}

  <CreateExampleModal bind:open={createOpen} questId={createQuestId} mode={createMode} reload={tabProps()} />

  <!-- The rated posts modal hides while this is open and comes back refreshed:
       the PATCH redirects to this URL, which carries rated_quest_id. -->
  <ChangeVideoThumbnailModal
    target={thumbnailTarget}
    reload={tabProps()}
    onchanged={() => (thumbnailTarget = null)}
    onclose={() => (thumbnailTarget = null)}
  />
  <ActionToast {toast} ondismiss={() => (toast = null)} />
</Layout>
